function CaseWorkflowTabs({ caseId, memberId, onOpenSmrForm }) {
  const [coreTrackId, setCoreTrackId] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)

  const load = React.useCallback(async () => {
    try {
      setLoading(true)
      const resp = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/workflows`))
      const json = await resp.json()
      if (json.success) {
        const tracks = json.data || []
        const core = tracks.find(t => (t.track?.track_key === 'CORE')) || tracks[0]
        setCoreTrackId(core?.track?.id || null)
      } else throw new Error(json.error || 'Failed to load workflows')
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [caseId])

  React.useEffect(() => { load() }, [load])

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-medium text-gray-900">Workflow</h3>
        <Link
          to={`/compliance-safe/ecdd?memberId=${encodeURIComponent(memberId || '')}`}
          className="btn-secondary text-sm"
        >
          Start ECDD
        </Link>
      </div>
      {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
      {loading ? (
        <div className="text-sm text-gray-500 py-4">Loading…</div>
      ) : coreTrackId ? (
        <CaseWorkflowTimeline caseId={caseId} trackId={coreTrackId} onOpenSmrForm={onOpenSmrForm} key={`track-${coreTrackId}`} />
      ) : (
        <div className="text-sm text-gray-600 mt-4">No workflows yet.</div>
      )}
    </div>
  )
}
import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  PlusIcon,
  ArrowTopRightOnSquareIcon,
  ChartBarIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import ProfileDetail from '../profile-safe/ProfileDetail'
import EventFlow from '../../components/EventFlow'
import SmrPreviewModal from '../../components/SmrPreviewModal'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatSmrReportDateTime } from '../../utils/smrDateDisplay'
import CaseCloseModal from './components/CaseCloseModal'

// API Configuration - Use proxy instead of direct backend calls

const CASE_STATUSES = [
  { value: 'OPEN', label: 'Open', color: 'blue', icon: ExclamationTriangleIcon },
  { value: 'INVESTIGATING', label: 'Investigating', color: 'yellow', icon: ClockIcon },
  { value: 'PENDING_REVIEW', label: 'Pending Review', color: 'purple', icon: DocumentTextIcon },
  { value: 'PENDING_APPROVAL', label: 'Pending Approval', color: 'orange', icon: UserGroupIcon },
  { value: 'RESOLVED', label: 'Resolved', color: 'green', icon: CheckCircleIcon },
  { value: 'CLOSED', label: 'Closed', color: 'gray', icon: XCircleIcon },
  { value: 'ESCALATED', label: 'Escalated', color: 'red', icon: ArrowTopRightOnSquareIcon }
]

const CASE_TYPES = [
  { value: 'SMR', label: 'SMR', description: 'Suspicious Matter Report (AUSTRAC)' },
  { value: 'ECDD', label: 'ECDD', description: 'Enhanced Customer Due Diligence' },
  { value: 'eKYC', label: 'eKYC', description: 'Enhanced Know Your Customer' },
  { value: 're-eKYC', label: 're-eKYC', description: 'Re-enhanced Know Your Customer' },
  { value: 'UAR', label: 'UAR', description: 'Unusual Activity Report' },
  { value: 'SAR', label: 'SAR', description: 'Suspicious Activity Report' },
  { value: 'CTR', label: 'CTR', description: 'Currency Transaction Report' },
  { value: 'SUSPICIOUS', label: 'Suspicious', description: 'General Suspicious Activity' }
]

const SEVERITY_LEVELS = [
  { value: 'LOW', label: 'Low', color: 'green' },
  { value: 'MEDIUM', label: 'Medium', color: 'yellow' },
  { value: 'HIGH', label: 'High', color: 'orange' },
  { value: 'CRITICAL', label: 'Critical', color: 'red' }
]

const TEAMS = [
  { value: 'AML_TEAM', label: 'AML Team' },
  { value: 'SECURITY_TEAM', label: 'Security Team' },
  { value: 'COMPLIANCE_TEAM', label: 'Compliance Team' },
  { value: 'FRAUD_TEAM', label: 'Fraud Team' }
]

function CaseDashboard() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const casesListPath = location.pathname.startsWith('/compliance-safe') ? '/compliance-safe/cases' : '/monitor-safe/cases'
  const { can } = useAuth()
  const [cases, setCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dashboardStats, setDashboardStats] = useState(null)
  const [selectedCase, setSelectedCase] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  // SMR Preview Modal state
  const [showSmrPreview, setShowSmrPreview] = React.useState(false)
  // Filter states — Case Management is for SMR cases in progress (filed SMRs go to SMR Registry)
  const [filters, setFilters] = useState({
    status: 'OPEN,INVESTIGATING,PENDING_REVIEW,PENDING_APPROVAL',
    case_type: 'SMR',
    severity: '',
    assigned_team: '',
    venue_id: '',
    search: '',
    created_from: '',
    created_to: '',
    unresolved_age_days_gte: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  const [venues, setVenues] = useState([])
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  })
  
  // Selected cases for bulk actions
  const [selectedCases, setSelectedCases] = useState(new Set())
  // Grouping
  const [groupBy, setGroupBy] = useState('none') // none | member | type | venue | age | treatment
  
  useEffect(() => {
    console.log('[CaseDashboard] useEffect triggered - filters:', JSON.stringify(filters), 'page:', pagination.page)
    loadCases()
    loadDashboardStats()
  }, [filters, pagination.page])
  
  useEffect(() => {
    loadVenues()
  }, [])

  const loadCases = async () => {
    try {
      setLoading(true)
      
      const queryParams = new URLSearchParams({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      })
      
      // Remove empty filters
      Object.keys(filters).forEach(key => {
        if (!filters[key]) queryParams.delete(key)
      })
      
      console.log('[CaseDashboard] Loading cases with filters:', filters, 'URL params:', queryParams.toString())
      const response = await authFetch(getLocalApiUrl(`/api/cases?${queryParams}`))
      const result = await response.json()
      
      if (result.success) {
        setCases(result.data)
        setPagination(prev => ({
          ...prev,
          total: result.pagination.total,
          totalPages: result.pagination.totalPages
        }))
      } else {
        throw new Error(result.error || 'Failed to load cases')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const loadDashboardStats = async () => {
    try {
      // Use same filters as list so stats match the cases shown
      const qs = new URLSearchParams()
      Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== '') qs.set(k, v) })
      const response = await authFetch(getLocalApiUrl(`/api/cases/dashboard/stats?${qs}`))
      const result = await response.json()
      
      if (result.success) {
        setDashboardStats(result.data)
      }
    } catch (err) {
      console.error('Error loading dashboard stats:', err)
    }
  }

  const loadVenues = async () => {
    try {
      const response = await authFetch(getLocalApiUrl('/api/venues'))
      const result = await response.json()
      if (result.success) {
        setVenues(result.data || [])
      }
    } catch (err) {
      console.error('Error loading venues:', err)
    }
  }

  const loadCaseDetails = async (caseId) => {
    try {
      const response = await authFetch(getLocalApiUrl(`/api/cases/${caseId}`))
      const result = await response.json()
      
      if (result.success) {
        setSelectedCase(result.data)
      } else {
        throw new Error(result.error || 'Failed to load case details')
      }
    } catch (err) {
      setError(err.message)
    }
  }

  // Deep-link support: open case when caseId is present in URL
  useEffect(() => {
    const caseId = searchParams.get('caseId')
    if (caseId && (!selectedCase || String(selectedCase.id) !== String(caseId))) {
      loadCaseDetails(caseId)
    }
  }, [searchParams])

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPagination(prev => ({ ...prev, page: 1 })) // Reset to first page
  }

  const clearFilters = () => {
    setFilters({
      status: 'OPEN,INVESTIGATING,PENDING_REVIEW,PENDING_APPROVAL',
      case_type: 'SMR',
      severity: '',
      assigned_team: '',
      venue_id: '',
      search: '',
      created_from: '',
      created_to: '',
      unresolved_age_days_gte: ''
    })
  }

  const toggleCaseSelection = (caseId) => {
    const newSelected = new Set(selectedCases)
    if (newSelected.has(caseId)) {
      newSelected.delete(caseId)
    } else {
      newSelected.add(caseId)
    }
    setSelectedCases(newSelected)
  }

  const selectAllCases = () => {
    if (selectedCases.size === cases.length) {
      setSelectedCases(new Set())
    } else {
      setSelectedCases(new Set(cases.map(c => c.id)))
    }
  }

  const getStatusInfo = (status) => {
    return CASE_STATUSES.find(s => s.value === status) || CASE_STATUSES[0]
  }

  const getSeverityColor = (severity) => {
    const severityInfo = SEVERITY_LEVELS.find(s => s.value === severity)
    return severityInfo?.color || 'gray'
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTimeSince = (dateString) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffMs = now - date
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)
    
    if (diffDays > 0) return `${diffDays}d ago`
    if (diffHours > 0) return `${diffHours}h ago`
    return 'Just now'
  }

  // Bucketing helpers for grouping
  const getAgeBucket = (dateString) => {
    const now = new Date()
    const created = new Date(dateString)
    const diffDays = Math.floor((now - created) / (1000*60*60*24))
    if (diffDays <= 1) return '0–1 day'
    if (diffDays <= 7) return '2–7 days'
    if (diffDays <= 30) return '8–30 days'
    return '>30 days'
  }

  const getGroupKey = (c) => {
    switch (groupBy) {
      case 'member': return (c.member_name && c.member_name.trim()) ? `${c.member_name}` : (c.member_id || 'Unidentified')
      case 'type': return c.case_type || 'Unknown'
      case 'age': return getAgeBucket(c.created_at)
      case 'treatment': return (c.assigned_team ? c.assigned_team.replace('_',' ') : 'None')
      case 'venue': return (c.venue_name || c.venue_code || 'Unassigned').trim() || 'Unassigned'
      default: return 'All'
    }
  }

  const grouped = React.useMemo(() => {
    if (groupBy === 'none') return null
    const map = new Map()
    for (const c of cases) {
      const k = getGroupKey(c)
      if (!map.has(k)) map.set(k, [])
      map.get(k).push(c)
    }
    // sort groups by key
    return Array.from(map.entries()).sort((a,b)=> String(a[0]).localeCompare(String(b[0])))
  }, [cases, groupBy])

  // Helper: compute SMR due chip for listing (3 business days from created_at)
  const addBusinessDaysList = (dateStr, days) => {
    const d = new Date(dateStr); const r = new Date(d); let a = 0;
    while (a < days) { r.setDate(r.getDate() + 1); const w = r.getDay(); if (w !== 0 && w !== 6) a++; }
    return r;
  }
  const renderSmrChip = (createdAtStr) => {
    try {
      const due = addBusinessDaysList(createdAtStr, 3)
      const now = new Date()
      const overdue = now > due
      const cls = overdue ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
      const dueStr = due.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
      const text = overdue ? `Overdue (was due ${dueStr})` : `SMR due by ${dueStr}`
      return (<span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium ${cls}`}>{text}</span>)
    } catch { return null }
  }

  if (loading && !cases.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Show create form
  if (showCreateForm) {
    return <CreateCaseForm onBack={() => setShowCreateForm(false)} onSuccess={() => {
      setShowCreateForm(false)
      loadCases()
      loadDashboardStats()
    }} />
  }

  // Show case details
  if (selectedCase) {
    return <CaseDetailView 
      caseData={selectedCase} 
      onBack={() => { setSelectedCase(null); navigate(casesListPath, { replace: true }) }}
      onRefresh={() => {
        loadCaseDetails(selectedCase.id)
        loadCases()
        loadDashboardStats()
      }}
      showSmrPreview={showSmrPreview}
      setShowSmrPreview={setShowSmrPreview}
      venues={venues}
    />
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Case Management</h1>
          <p className="text-gray-500">SMR cases in progress — filed SMRs are in SMR Registry</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary flex items-center"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          <button 
            onClick={() => setShowCreateForm(true)}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Case
          </button>
        </div>
      </div>

      {/* Quick status tabs & grouping */}
      <div className="flex gap-2">
        {[
          {key:'OPEN,INVESTIGATING,PENDING_REVIEW,PENDING_APPROVAL', label:'All'},
          {key:'OPEN', label:'Open'},
          {key:'INVESTIGATING', label:'In Progress'},
          {key:'RESOLVED', label:'Resolved'}
        ].map(t => (
          <button key={t.key||'ALL'}
            onClick={()=>handleFilterChange('status', t.key)}
            className={`px-3 py-1 rounded-md text-sm border ${filters.status===t.key? 'bg-psg-teal text-white border-psg-teal':'bg-white text-gray-700 border-gray-300'}`}
          >{t.label}</button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-sm text-gray-600">Group by</span>
          <select value={groupBy} onChange={(e)=>setGroupBy(e.target.value)} className="select-field text-sm">
            <option value="none">None</option>
            <option value="member">Member</option>
            <option value="type">Type</option>
            <option value="venue">Venue</option>
            <option value="age">Age</option>
            <option value="treatment">Treatment</option>
          </select>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboardStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Open Cases</p>
                <p className="text-2xl font-semibold text-gray-900">{dashboardStats.open_cases}</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Investigating</p>
                <p className="text-2xl font-semibold text-gray-900">{dashboardStats.investigating}</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Resolved</p>
                <p className="text-2xl font-semibold text-gray-900">{dashboardStats.resolved_cases || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="card">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="select-field"
              >
                <option value="">All Statuses</option>
                {CASE_STATUSES.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Case Type</label>
              <select
                value={filters.case_type}
                onChange={(e) => handleFilterChange('case_type', e.target.value)}
                className="select-field"
              >
                <option value="">All Types</option>
                {CASE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
              <select
                value={filters.severity}
                onChange={(e) => handleFilterChange('severity', e.target.value)}
                className="select-field"
              >
                <option value="">All Severities</option>
                {SEVERITY_LEVELS.map(severity => (
                  <option key={severity.value} value={severity.value}>{severity.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
              <select
                value={filters.assigned_team}
                onChange={(e) => handleFilterChange('assigned_team', e.target.value)}
                className="select-field"
              >
                <option value="">All Teams</option>
                {TEAMS.map(team => (
                  <option key={team.value} value={team.value}>{team.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Venue</label>
              <select
                value={filters.venue_id}
                onChange={(e) => handleFilterChange('venue_id', e.target.value)}
                className="select-field"
              >
                <option value="">All Venues</option>
                {venues.map(venue => (
                  <option key={venue.id} value={venue.id}>{venue.name} ({venue.code})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unresolved Age ≥ (days)</label>
              <input
                type="number"
                min={0}
                value={filters.unresolved_age_days_gte}
                onChange={(e) => handleFilterChange('unresolved_age_days_gte', e.target.value)}
                className="input-field"
                placeholder="e.g., 7"
              />
            </div>
          </div>
          
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <input
                  type="text"
                  placeholder="Search cases..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="input-field"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={filters.created_from}
                  onChange={(e) => handleFilterChange('created_from', e.target.value)}
                  className="input-field"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={filters.created_to}
                  onChange={(e) => handleFilterChange('created_to', e.target.value)}
                  className="input-field"
                />
              </div>
            </div>
            <button
              onClick={clearFilters}
              className="btn-secondary text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Cases Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Cases ({pagination.total})
            </h3>
            {selectedCases.size > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  {selectedCases.size} selected
                </span>
                <button className="btn-secondary text-sm">Bulk Actions</button>
              </div>
            )}
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedCases.size === cases.length && cases.length > 0}
                    onChange={selectAllCases}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Case
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type & Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Venue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(groupBy !== 'none' && grouped) ? (
                grouped.map(([groupKey, items]) => (
                  <React.Fragment key={groupKey}>
                    <tr className="bg-gray-50">
                      <td colSpan={9} className="px-6 py-2 text-sm font-semibold text-gray-700">{groupKey} • {items.length}</td>
                    </tr>
                    {items.map((caseItem) => {
                      const statusInfo = getStatusInfo(caseItem.status)
                      const StatusIcon = statusInfo.icon
                      return (
                        <tr key={caseItem.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input type="checkbox" checked={selectedCases.has(caseItem.id)} onChange={() => toggleCaseSelection(caseItem.id)} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <button type="button" onClick={() => loadCaseDetails(caseItem.id)} className="text-sm font-medium text-primary-600 hover:text-primary-900 hover:underline text-left">{caseItem.case_number}</button>
                              <div className="text-sm text-gray-500 truncate max-w-xs">{caseItem.rule_name}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{caseItem.member_id || 'Unidentified'}</div>
                              {caseItem.member_name ? (<div className="text-sm text-gray-500">{caseItem.member_name}</div>) : !caseItem.member_id && caseItem.summary && (<div className="text-sm text-gray-500 truncate max-w-[200px]" title={caseItem.summary}>{caseItem.summary.slice(0, 40)}…</div>)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col space-y-1">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${caseItem.case_type === 'SMR' ? 'bg-purple-100 text-purple-800' : caseItem.case_type === 'UAR' ? 'bg-amber-100 text-amber-800' : caseItem.case_type === 'ECDD' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>{caseItem.case_type}</span>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${getSeverityColor(caseItem.severity)}-100 text-${getSeverityColor(caseItem.severity)}-800`}>{caseItem.severity}</span>
                              {caseItem.processing_category && (<span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium ${caseItem.processing_category === 'cumulative' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}>{caseItem.processing_category === 'cumulative' ? 'Cumulative' : 'Session'}</span>)}
                              {renderSmrChip(caseItem.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <StatusIcon className={`h-4 w-4 text-${statusInfo.color}-500 mr-2`} />
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}>{statusInfo.label}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{caseItem.assigned_team?.replace('_', ' ') || 'Unassigned'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {caseItem.venue_name ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                {caseItem.venue_code || ''} {caseItem.venue_name}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm text-gray-900">{formatDate(caseItem.created_at)}</div>
                              <div className="text-sm text-gray-500">{getTimeSince(caseItem.created_at)}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => loadCaseDetails(caseItem.id)}
                                className="text-primary-600 hover:text-primary-900"
                                title="View Case"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button className="text-gray-400 hover:text-gray-600" title="Generate Report"><DocumentTextIcon className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </React.Fragment>
                ))
              ) : (
              cases.map((caseItem) => {
                const statusInfo = getStatusInfo(caseItem.status)
                const StatusIcon = statusInfo.icon
                
                return (
                  <tr key={caseItem.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedCases.has(caseItem.id)}
                        onChange={() => toggleCaseSelection(caseItem.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <button type="button" onClick={() => loadCaseDetails(caseItem.id)} className="text-sm font-medium text-primary-600 hover:text-primary-900 hover:underline text-left">
                          {caseItem.case_number}
                        </button>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {caseItem.rule_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {caseItem.member_id || 'Unidentified'}
                        </div>
                        {caseItem.member_name ? (
                          <div className="text-sm text-gray-500">
                            {caseItem.member_name}
                          </div>
                        ) : !caseItem.member_id && caseItem.summary ? (
                          <div className="text-sm text-gray-500 truncate max-w-[200px]" title={caseItem.summary}>
                            {caseItem.summary.slice(0, 40)}…
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          caseItem.case_type === 'SMR' ? 'bg-purple-100 text-purple-800' :
                          caseItem.case_type === 'UAR' ? 'bg-amber-100 text-amber-800' :
                          caseItem.case_type === 'ECDD' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {caseItem.case_type}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${getSeverityColor(caseItem.severity)}-100 text-${getSeverityColor(caseItem.severity)}-800`}>
                          {caseItem.severity}
                        </span>
                         {caseItem.processing_category && (
                           <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium ${caseItem.processing_category === 'cumulative' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}>
                             {caseItem.processing_category === 'cumulative' ? 'Cumulative' : 'Session'}
                           </span>
                         )}
                        {renderSmrChip(caseItem.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <StatusIcon className={`h-4 w-4 text-${statusInfo.color}-500 mr-2`} />
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusInfo.color}-100 text-${statusInfo.color}-800`}>
                          {statusInfo.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {caseItem.assigned_team?.replace('_', ' ') || 'Unassigned'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {caseItem.venue_name ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {caseItem.venue_code || ''} {caseItem.venue_name}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">
                          {formatDate(caseItem.created_at)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {getTimeSince(caseItem.created_at)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => loadCaseDetails(caseItem.id)}
                          className="text-primary-600 hover:text-primary-900"
                          title="View Case"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600" title="Generate Report">
                          <DocumentTextIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              }))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="btn-secondary"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="btn-secondary"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">{pagination.total}</span>{' '}
                    results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                      disabled={pagination.page === pagination.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Create Case Form Component
function CreateCaseForm({ onBack, onSuccess }) {
  const [formData, setFormData] = useState({
    rule_id: '',
    rule_name: '',
    rule_type: 'transaction',
    processing_category: 'session-based',
    case_type: 'SMR',
    category: 'AML',
    severity: 'MEDIUM',
    member_id: '',
    member_name: '',
    member_tier: 'Bronze',
    source_data_type: 'transaction',
    source_data_id: '',
    description: '',
    assigned_team: 'COMPLIANCE_TEAM'
  })

  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    
    try {
      const response = await fetch(getLocalApiUrl('/api/cases'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source_data_snapshot: {
            description: formData.description,
            created_manually: true,
            source_data_id: formData.source_data_id
          },
          triggered_by: 'manual'
        })
      })
      
      const result = await response.json()
      if (result.success) {
        onSuccess()
      } else {
        throw new Error(result.error || 'Failed to create case')
      }
    } catch (error) {
      console.error('Error creating case:', error)
      alert('Error creating case: ' + error.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Create New Case</h3>
          <button
            onClick={onBack}
            className="text-gray-400 hover:text-gray-600"
          >
            ← Back to Dashboard
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Rule Information */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Rule Information</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Rule ID *</label>
                  <input
                    type="text"
                    value={formData.rule_id}
                    onChange={(e) => handleChange('rule_id', e.target.value)}
                    className="input-field"
                    placeholder="e.g., MANUAL_001"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Rule Name *</label>
                  <input
                    type="text"
                    value={formData.rule_name}
                    onChange={(e) => handleChange('rule_name', e.target.value)}
                    className="input-field"
                    placeholder="e.g., Manual Investigation"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Rule Type</label>
                  <select
                    value={formData.rule_type}
                    onChange={(e) => handleChange('rule_type', e.target.value)}
                    className="select-field"
                  >
                    <option value="transaction">Transaction</option>
                    <option value="session">Session</option>
                    <option value="onboarding">Onboarding</option>
                    <option value="ongoing">Ongoing</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Processing Category</label>
                  <select
                    value={formData.processing_category}
                    onChange={(e) => handleChange('processing_category', e.target.value)}
                    className="select-field"
                  >
                    <option value="session-based">Session-based</option>
                    <option value="cumulative">Cumulative</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Case Classification */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Case Classification</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Case Type</label>
                  <select
                    value={formData.case_type}
                    onChange={(e) => handleChange('case_type', e.target.value)}
                    className="select-field"
                  >
                    {CASE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    className="input-field"
                    placeholder="e.g., AML, FRAUD, KYC"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Severity</label>
                  <select
                    value={formData.severity}
                    onChange={(e) => handleChange('severity', e.target.value)}
                    className="select-field"
                  >
                    {SEVERITY_LEVELS.map(severity => (
                      <option key={severity.value} value={severity.value}>{severity.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Assigned Team</label>
                  <select
                    value={formData.assigned_team}
                    onChange={(e) => handleChange('assigned_team', e.target.value)}
                    className="select-field"
                  >
                    {TEAMS.map(team => (
                      <option key={team.value} value={team.value}>{team.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Member Information */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Member Information</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Member ID *</label>
                  <input
                    type="text"
                    value={formData.member_id}
                    onChange={(e) => handleChange('member_id', e.target.value)}
                    className="input-field"
                    placeholder="e.g., M123456"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Member Name</label>
                  <input
                    type="text"
                    value={formData.member_name}
                    onChange={(e) => handleChange('member_name', e.target.value)}
                    className="input-field"
                    placeholder="e.g., John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Member Tier</label>
                  <select
                    value={formData.member_tier}
                    onChange={(e) => handleChange('member_tier', e.target.value)}
                    className="select-field"
                  >
                    <option value="Bronze">Bronze</option>
                    <option value="Silver">Silver</option>
                    <option value="Gold">Gold</option>
                    <option value="Platinum">Platinum</option>
                    <option value="Diamond">Diamond</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Source Data */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-4">Source Data</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Source Data Type</label>
                  <select
                    value={formData.source_data_type}
                    onChange={(e) => handleChange('source_data_type', e.target.value)}
                    className="select-field"
                  >
                    <option value="transaction">Transaction</option>
                    <option value="session">Session</option>
                    <option value="member">Member</option>
                    <option value="event">Event</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Source Data ID *</label>
                  <input
                    type="text"
                    value={formData.source_data_id}
                    onChange={(e) => handleChange('source_data_id', e.target.value)}
                    className="input-field"
                    placeholder="e.g., TX123456 or S789012"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={4}
                    className="input-field"
                    placeholder="Describe the case and any relevant details..."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onBack}
              className="btn-secondary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Case'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Case Detail View Component (will be added in next part)
function CaseDetailView({ caseData, onBack, onRefresh, showSmrPreview, setShowSmrPreview, venues = [] }) {
  const { can } = useAuth()
  const [caseMeta, setCaseMeta] = React.useState(null)
  const [savingMeta, setSavingMeta] = React.useState(false)
  const [showProfile, setShowProfile] = React.useState(false)
  // S8 — structured decision reasoning when closing after investigation.
  const [pendingClose, setPendingClose] = React.useState(null) // { kind }
  const [closeSubmitting, setCloseSubmitting] = React.useState(false)
  const [linkedAlert, setLinkedAlert] = React.useState(null)
  const [relatedSession, setRelatedSession] = React.useState(null)
  const [showSessionFlow, setShowSessionFlow] = React.useState(false)
  const [contextItem, setContextItem] = React.useState(null)
  const [evidenceForTimeline, setEvidenceForTimeline] = React.useState(null)
  const [showContext, setShowContext] = React.useState(false)
  const [showSmrForm, setShowSmrForm] = React.useState(false)
  const [smrDraft, setSmrDraft] = React.useState(null)
  const [smrLookups, setSmrLookups] = React.useState({ offences: [], services: [], reasons: [] })
  // Load lookups for Case meta panel (offence types, designated services, suspicion reasons)
  React.useEffect(() => {
    (async () => {
      try {
        const [off, svc, rsn] = await Promise.all([
          authFetch(getLocalApiUrl('/api/lookups/offence-types')).then(r => r.json()),
          authFetch(getLocalApiUrl('/api/lookups/designated-services')).then(r => r.json()),
          authFetch(getLocalApiUrl('/api/lookups/suspicion-reasons')).then(r => r.json())
        ])
        setSmrLookups(prev => ({
          offences: off && off.success ? off.data : prev.offences,
          services: svc && svc.success ? svc.data : prev.services,
          reasons: rsn && rsn.success ? rsn.data : prev.reasons
        }))
      } catch (_) { /* ignore lookup load errors in Case view */ }
    })()
  }, [])
  const [taskModalOpen, setTaskModalOpen] = React.useState(false)
  const [staff, setStaff] = React.useState([])
  const [taskForm, setTaskForm] = React.useState({ title:'', description:'', assignee:'', due_date:'', priority:'medium' })
  const [caseTasks, setCaseTasks] = React.useState([])
  const loadCaseTasks = React.useCallback(async ()=>{ try { const r = await authFetch(getLocalApiUrl(`/api/governance/tasks?category=case&level=case&entity_id=${encodeURIComponent(caseData.id)}`)); const j = await r.json(); if (j.success) setCaseTasks(j.data||[]) } catch {} }, [caseData.id])
  React.useEffect(()=>{ loadCaseTasks() }, [loadCaseTasks])
  React.useEffect(()=>{ (async()=>{ try { const r = await authFetch(getLocalApiUrl('/api/staff')); const j = await r.json(); if (j.success) setStaff(j.data||[]) } catch {} })() }, [])
  const [evidenceReloadKey, setEvidenceReloadKey] = React.useState(0)
  const snap = caseData?.source_data_snapshot || {}
  React.useEffect(() => {
    const fetchLinkedAlert = async () => {
      try {
        const resp = await authFetch(getLocalApiUrl(`/api/alerts/by-case/${caseData.id}`))
        const json = await resp.json()
        if (json.success) setLinkedAlert(json.data)
      } catch (e) {
        // ignore
      }
    }
    fetchLinkedAlert()
  }, [caseData?.id])
  const ctx = contextItem || snap
  const start = ctx.StartTime || ctx.SessionStart || ctx.sessionStart
  const end = ctx.EndTime || ctx.SessionEnd || ctx.sessionEnd

  React.useEffect(() => {
    const tid = snap.TransactionPK || caseData.source_data_id
    if (!tid) return
    const fetchRelatedSession = async () => {
      try {
        const resp = await authFetch(getLocalApiUrl(`/api/transactions/${tid}/related-session`))
        const json = await resp.json()
        if (json.success) setRelatedSession(json.data)
      } catch (_) {}
    }
    fetchRelatedSession()
  }, [snap.TransactionPK, caseData.source_data_id])

  const openSmrForm = async () => {
    try {
      const [draftResp, off, svc, rsn] = await Promise.all([
        authFetch(getLocalApiUrl(`/api/cases/${caseData.id}/forms/smr`)).then(r=>r.json()),
        authFetch(getLocalApiUrl('/api/lookups/offence-types')).then(r=>r.json()),
        authFetch(getLocalApiUrl('/api/lookups/designated-services')).then(r=>r.json()),
        authFetch(getLocalApiUrl('/api/lookups/suspicion-reasons')).then(r=>r.json())
      ])
      if (draftResp.success) setSmrDraft(draftResp.data)
      setSmrLookups({
        offences: off.success ? off.data : [],
        services: svc.success ? svc.data : [],
        reasons: rsn.success ? rsn.data : []
      })
      setShowSmrForm(true)
    } catch (_) {
      setShowSmrForm(true)
    }
  }
  // Load/save Case meta (SMR fields)
  React.useEffect(() => { (async()=>{ try { const r = await authFetch(getLocalApiUrl(`/api/cases/${caseData.id}/forms/case`)); const j = await r.json(); if (j.success) setCaseMeta(j.data || { suspicion:{}, flags:{} }) } catch { setCaseMeta({ suspicion:{}, flags:{} }) } })() }, [caseData.id])
  const addBusinessDays = (d, days) => { const r=new Date(d); let a=0; while(a<days){ r.setDate(r.getDate()+1); const w=r.getDay(); if(w!==0&&w!==6) a++; } return r }
  const suspectedTF = !!(caseMeta && caseMeta.flags && caseMeta.flags.suspectedTF)
  const createdAt = new Date(caseData.created_at)
  const dueAt = suspectedTF ? new Date(createdAt.getTime()+24*60*60*1000) : addBusinessDays(createdAt,3)
  const now = new Date()
  const overdue = now > dueAt
  const remainingMs = Math.max(0, dueAt-now)
  const remainingDays = Math.floor(remainingMs/(24*60*60*1000))
  const remainingHours = Math.floor((remainingMs%(24*60*60*1000))/(60*60*1000))
  const saveCaseMeta = async () => { try { setSavingMeta(true); const r = await authFetch(getLocalApiUrl(`/api/cases/${caseData.id}/forms/case`), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(caseMeta||{}) }); const j = await r.json(); if(!j.success) throw new Error(j.error||'Failed to save'); } catch(e){ alert(e.message||'Failed to save') } finally { setSavingMeta(false) } }

  const saveSmrDraft = async (data) => {
    try {
      await authFetch(getLocalApiUrl(`/api/cases/${caseData.id}/forms/smr`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
      setSmrDraft(data)
    } catch (_) {}
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <CaseCloseModal
        open={!!pendingClose}
        previousStatus={caseData.status}
        submitting={closeSubmitting}
        onCancel={() => setPendingClose(null)}
        onConfirm={async ({ resolution_status, reason }) => {
          setCloseSubmitting(true)
          try {
            const res = await authFetch(getLocalApiUrl(`/api/cases/${caseData.id}/status`), {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'CLOSED', resolution_status, reason }),
            })
            const json = await res.json()
            if (json.success) {
              setPendingClose(null)
              alert('✓ Case closed')
              if (typeof onRefresh === 'function') onRefresh(caseData.id)
            } else {
              alert('Failed to close: ' + (json.error || 'Unknown error'))
            }
          } catch (err) {
            alert('Error: ' + err.message)
          } finally {
            setCloseSubmitting(false)
          }
        }}
      />
      <div className="card">
        <div className="flex justify-between items-start">
          <div>
            <button onClick={onBack} className="text-psg-teal hover:underline mb-2">← Back to Dashboard</button>
            <h1 className="text-2xl font-bold text-gray-900">{caseData.case_number}</h1>
            <p className="text-sm text-gray-500 mt-1">{caseData.rule_name}</p>
          </div>
          <div className="text-right space-y-2">
            <div className="flex items-center justify-end gap-2">
              <span className="text-xs text-gray-500">Status:</span>
              <select
                value={caseData.status}
                onChange={async (e) => {
                  const newStatus = e.target.value
                  // S8 — post-investigation close requires a structured
                  // decision. Route through the modal for any CLOSED
                  // transition except straight from OPEN (quick dismiss).
                  const previousStatus = caseData.status
                  if (newStatus === 'CLOSED' && previousStatus && previousStatus !== 'OPEN') {
                    setPendingClose({ kind: 'header-dropdown' })
                    return
                  }
                  try {
                    const res = await authFetch(getLocalApiUrl(`/api/cases/${caseData.id}/status`), {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ status: newStatus, reason: 'Status updated from dashboard', user_id: 'web_user' })
                    })
                    const json = await res.json()
                    if (json.success) {
                      alert('✓ Status updated')
                      if (typeof onRefresh === 'function') onRefresh(caseData.id)
                    } else {
                      alert('Failed: ' + (json.error || 'Unknown error'))
                    }
                  } catch (e) {
                    alert('Error: ' + e.message)
                  }
                }}
                className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border-0 cursor-pointer"
              >
                <option value="OPEN">OPEN</option>
                <option value="INVESTIGATING">INVESTIGATING</option>
                <option value="PENDING_REVIEW">PENDING REVIEW</option>
                <option value="RESOLVED">RESOLVED</option>
                <option value="CLOSED">CLOSED</option>
              </select>
            </div>
            <p className="text-sm text-gray-500 mt-1">Risk Score: {caseData.risk_score != null ? caseData.risk_score : '—'}</p>
            <div className={`text-xs px-2 py-1 rounded inline-block ${overdue ? 'bg-red-100 text-red-800' : (suspectedTF ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800')}`}>
              {overdue
                ? `Overdue (was due ${dueAt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })})`
                : (suspectedTF
                    ? `Due by ${dueAt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} (24h)`
                    : `Due by ${dueAt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} (3 business days)`)}
            </div>
            <div>
              <div className="flex items-center gap-2 justify-end">
                {can('case.smr','edit') && (
                  <button
                    className="btn-secondary text-sm"
                    onClick={openSmrForm}
                    title="Open SMR Form"
                  >Open SMR Form</button>
                )}
                {can('case.smr','download') && (
                  <button
                    className="btn-primary text-sm"
                    onClick={() => setShowSmrPreview(true)}
                    title="Preview SMR Report"
                  >Preview & Download SMR</button>
                )}
                {false && (
                <button
                  className="btn-secondary text-sm"
                  onClick={() => { setEvidenceReloadKey(Date.now()); onRefresh && onRefresh() }}
                  title="Re-evaluate"
                >Re-evaluate</button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Workflow moved to left column as vertical timeline */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left narrow column */}
        <div className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Case Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500">Member ID</div>
                <div className="mt-1 text-sm font-mono text-gray-900">
                  {caseData.member_id ? (
                    <button className="text-psg-teal hover:underline" onClick={()=>setShowProfile(true)}>{caseData.member_id}</button>
                  ) : (
                    <span className="text-amber-700 font-medium">Unidentified</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Member Name</div>
                <div className="mt-1 text-sm text-gray-900">{caseData.member_name || (caseData.member_id ? 'N/A' : 'Activity-based (no patron yet)')}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Case Type</div>
                <div className="mt-1 text-sm text-gray-900">{caseData.case_type}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Severity</div>
                <div className="mt-1"><span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${caseData.severity === 'CRITICAL' ? 'bg-red-100 text-red-800' : caseData.severity === 'HIGH' ? 'bg-orange-100 text-orange-800' : caseData.severity === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>{caseData.severity}</span></div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Assigned Team</div>
                <div className="mt-1 text-sm text-gray-900">{caseData.assigned_team?.replace('_', ' ')}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Venue</div>
                <div className="mt-1">
                  <select
                    className="select-field text-sm w-full"
                    value={caseData.venue_id || ''}
                    onChange={async (e) => {
                      const newVenueId = e.target.value || null;
                      try {
                        const resp = await authFetch(getLocalApiUrl(`/api/cases/${caseData.id}/venue`), {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ venue_id: newVenueId })
                        });
                        if (resp.ok) {
                          loadCaseDetails(caseData.id);
                          loadCases();
                        }
                      } catch (err) {
                        console.error('Failed to update venue:', err);
                      }
                    }}
                  >
                    <option value="">— Select Venue —</option>
                    {venues.map(v => (
                      <option key={v.id} value={v.id}>{v.name} ({v.code})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Created</div>
                <div className="mt-1 text-sm text-gray-900">{new Date(caseData.created_at).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-500">Closed</div>
                <div className="mt-1 text-sm text-gray-900">{caseData.closed_at ? new Date(caseData.closed_at).toLocaleString() : '—'}</div>
              </div>
            </div>
          </div>

          {/* Overdue SMR: clarify priority — complete draft and file, don't wait for workflow approval */}
          {caseData.case_type === 'SMR' && overdue && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
              <div className="text-sm font-semibold text-red-800">⚠ SMR filing deadline passed</div>
              <div className="text-xs text-red-700 mt-1">Complete the SMR draft above and file immediately. Do not wait for workflow approval.</div>
            </div>
          )}

          {/* Vertical workflow timeline below Case Details */}
          <CaseWorkflowTabs caseId={caseData.id} memberId={caseData.member_id} onOpenSmrForm={openSmrForm} />

          {/* Next Action moved to left column; visible for open/in-progress even without alert */}
          {/* Next Action panel removed per new workflow integration */}

          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Statistics</h3>
            <div className="space-y-3">
              <div className="flex justify-between"><span className="text-sm text-gray-500">Days Open</span><span className="text-sm font-medium text-gray-900">{Math.ceil((new Date() - new Date(caseData.created_at)) / (1000 * 60 * 60 * 24))}</span></div>
              <div className="flex justify-between"><span className="text-sm text-gray-500">Activities</span><span className="text-sm font-medium text-gray-900">{caseData.activities?.length || 0}</span></div>
              <div className="flex justify-between"><span className="text-sm text-gray-500">Risk Score</span><span className="text-sm font-medium text-gray-900">{caseData.risk_score != null ? caseData.risk_score : '—'}</span></div>
            </div>
          </div>
        </div>

        {/* Right wide column */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">Case (SMR) Fields</h3>
              <div className="flex items-center gap-2">
                <button className="btn-secondary" onClick={saveCaseMeta} disabled={savingMeta}>{savingMeta ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Reason for Suspicion (other)</label>
                <input className="input-field w-full" value={caseMeta?.suspicion?.reasonOther || ''} onChange={e=> setCaseMeta(m=>({ ...(m||{}), suspicion:{ ...(m?.suspicion||{}), reasonOther: e.target.value } }))} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Nature (transaction/behaviour)</label>
                <input className="input-field w-full" value={caseMeta?.nature || ''} onChange={e=> setCaseMeta(m=>({ ...(m||{}), nature: e.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Offence Type</label>
                <select
                  className="select-field w-full"
                  value={caseMeta?.offence_type || ''}
                  onChange={(e)=> setCaseMeta(m=>({ ...(m||{}), offence_type: e.target.value }))}
                >
                  <option value="">Select an offence</option>
                  {(smrLookups?.offences || []).map(o => (
                    <option key={o.code} value={o.code}>{o.description || o.code}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Designated Services</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(smrLookups?.services || []).map(s => {
                    const selected = Array.isArray(caseMeta?.designated_services) && caseMeta.designated_services.includes(s.code)
                    return (
                      <label key={s.code} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!selected}
                          onChange={(e)=> setCaseMeta(m=>{
                            const current = Array.isArray(m?.designated_services) ? m.designated_services : []
                            const next = e.target.checked ? Array.from(new Set([...current, s.code])) : current.filter(c => c !== s.code)
                            return { ...(m||{}), designated_services: next }
                          })}
                        />
                        <span className="text-sm">{s.description || s.code}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Suspicion Reasons</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(smrLookups?.reasons || []).map(r => {
                    const selected = Array.isArray(caseMeta?.suspicion?.reasons) && caseMeta.suspicion.reasons.includes(r.code)
                    return (
                      <label key={r.code} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!selected}
                          onChange={(e)=> setCaseMeta(m=>{
                            const base = m?.suspicion || {}
                            const current = Array.isArray(base.reasons) ? base.reasons : []
                            const next = e.target.checked ? Array.from(new Set([...current, r.code])) : current.filter(c => c !== r.code)
                            return { ...(m||{}), suspicion: { ...base, reasons: next } }
                          })}
                        />
                        <span className="text-sm">{r.description || r.code}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Grounds for Suspicion</label>
                <textarea rows={4} className="input-field w-full" value={caseMeta?.suspicion?.grounds || ''} onChange={e=> setCaseMeta(m=>({ ...(m||{}), suspicion:{ ...(m?.suspicion||{}), grounds: e.target.value } }))} />
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <input id="tf-flag" type="checkbox" checked={!!(caseMeta?.flags?.suspectedTF)} onChange={e=> setCaseMeta(m=>({ ...(m||{}), flags:{ ...(m?.flags||{}), suspectedTF: e.target.checked } }))} />
                <label htmlFor="tf-flag" className="text-sm text-gray-700">Suspected Terrorism Financing (24h window)</label>
              </div>
            </div>
          </div>
          {/* Evidence + Select Transactions tabs */}
          <EvidenceWithTransactionsPanel
            key={evidenceReloadKey}
            caseId={caseData.id}
            onSelectItem={(it) => { setContextItem(it); setShowContext(true) }}
            onEvidenceLoaded={(data) => {
              setEvidenceForTimeline(data)
              if (data && Array.isArray(data.items) && data.items.length === 1) {
                setContextItem(data.items[0])
                setShowContext(true)
              }
            }}
            onEvidenceAdded={() => setEvidenceReloadKey(k => k + 1)}
          />
          {/* Previous SMRs (same member) - before Attachments */}
          <PreviousSmrsPanel caseId={caseData.id} />
          {/* Previous ECDD (same member) */}
          <PreviousEcddPanel memberId={caseData.member_id} memberName={caseData.member_name} />
          {/* Attachments */}
          <CaseAttachmentsPanel caseId={caseData.id} />
          {/* Case Tasks Panel */}
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">Case Tasks</h3>
              <button className="btn-primary" onClick={()=>setTaskModalOpen(true)}>Add</button>
            </div>
            <div className="mt-4">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-gray-500 uppercase text-xs">
                      <th className="text-left p-3">Title</th>
                      <th className="text-left p-3">Due</th>
                      <th className="text-left p-3">Assignee</th>
                      <th className="text-left p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(caseTasks||[]).map(t => (
                      <tr key={t.task_id} className="border-b">
                        <td className="p-3">{t.title}</td>
                        <td className="p-3">{t.due_date || '—'}</td>
                        <td className="p-3">{t.assignee || '—'}</td>
                        <td className="p-3">{t.status}</td>
                      </tr>
                    ))}
                    {(!caseTasks || caseTasks.length===0) && (
                      <tr><td className="p-3 text-gray-500" colSpan={4}>No tasks</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* Evidence first */}
          

          {/* Transaction / Session Context removed in favor of per-evidence expanded view */}

          {/* Linked Alert (if any) */}
          {linkedAlert && (
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Linked Alert</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Alert Type</div>
                  <div className="font-mono">{linkedAlert.alert_type}</div>
                </div>
                <div>
                  <div className="text-gray-500">Severity</div>
                  <div>{(linkedAlert.severity||'').toUpperCase()}</div>
                </div>
                <div>
                  <div className="text-gray-500">First Seen</div>
                  <div>{new Date(linkedAlert.first_seen).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-gray-500">Last Updated</div>
                  <div>{new Date(linkedAlert.last_updated).toLocaleString()}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500 mb-1">Alert Details</div>
                  <pre className="text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-64">{JSON.stringify(linkedAlert.details || {}, null, 2)}</pre>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Activity Timeline</h3>
            {caseData.activities && caseData.activities.length > 0 ? (
              <div className="space-y-3">
                {caseData.activities.map((activity, index) => (
                  <div key={activity.id || index} className="flex space-x-3">
                    <div className="flex-shrink-0"><DocumentTextIcon className="h-5 w-5 text-gray-400" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                      <p className="text-sm text-gray-500">{activity.description}</p>
                      <p className="text-xs text-gray-400 mt-1">{activity.created_by} • {new Date(activity.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No activities recorded yet.</p>
            )}
          </div>

          {/* Next Action moved left; removed from here */}
        </div>
      </div>

      {showProfile && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30" onClick={()=>setShowProfile(false)}></div>
          <div className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-auto">
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b bg-white">
              <div>
                <div className="text-xs text-gray-500">Member</div>
                <div className="text-lg font-semibold font-mono">{caseData.member_id}</div>
              </div>
              <button className="p-2 rounded hover:bg-gray-100" onClick={()=>setShowProfile(false)}>
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <ProfileDetail profileId={caseData.member_id} onBack={() => setShowProfile(false)} />
          </div>
        </div>
      )}

      {showSmrForm && (
        <SmrFormDrawer
          draft={smrDraft}
          lookups={smrLookups}
          onClose={() => setShowSmrForm(false)}
          onSave={saveSmrDraft}
          caseId={caseData.id}
        />
      )}
      {/* SMR Preview Modal */}
      <SmrPreviewModal
        caseId={caseData?.id}
        isOpen={showSmrPreview}
        onClose={() => setShowSmrPreview(false)}
      />
      {taskModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={()=>setTaskModalOpen(false)}></div>
          <div className="relative z-10 bg-white w-full max-w-3xl rounded-lg shadow-lg">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Create Case Task</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={()=>setTaskModalOpen(false)}>✕</button>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">To</label>
                    <select className="select-field" value={taskForm.assignee} onChange={(e)=>setTaskForm(p=>({ ...p, assignee:e.target.value }))}>
                      <option value="">Unassigned</option>
                      {(staff||[]).map(s=> <option key={s.id||s.staff_id||s.name} value={s.name}>{s.name}{s.role?` — ${s.role}`:''}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Task title</label>
                    <input className="input-field" value={taskForm.title} onChange={(e)=>setTaskForm(p=>({ ...p, title:e.target.value }))} placeholder="Task title" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Description</label>
                    <textarea className="input-field" rows={6} value={taskForm.description} onChange={(e)=>setTaskForm(p=>({ ...p, description:e.target.value }))} placeholder="Describe the task..." />
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Due date</label>
                    <input type="date" className="input-field" value={taskForm.due_date} onChange={(e)=>setTaskForm(p=>({ ...p, due_date:e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Priority</label>
                    <select className="select-field" value={taskForm.priority} onChange={(e)=>setTaskForm(p=>({ ...p, priority:e.target.value }))}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Attachment</label>
                    <input type="file" className="input-field" onChange={(e)=>setTaskForm(p=>({ ...p, _file: e.target.files?.[0] || null }))} />
                  </div>
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
              <button className="btn-outline" onClick={()=>setTaskModalOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={async()=>{ try { const resp = await authFetch(getLocalApiUrl('/api/governance/tasks'), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ title: taskForm.title || 'Case Task', description: taskForm.description || null, category:'case', level:'case', entity_id: String(caseData.id), due_date: taskForm.due_date || null, assignee: taskForm.assignee || null, priority: taskForm.priority }) }); const j = await resp.json(); if (j.success) { if (taskForm._file) { const fd = new FormData(); fd.append('file', taskForm._file); await authFetch(getLocalApiUrl(`/api/governance/tasks/${j.data.task_id}/attachments`), { method:'POST', body: fd }) } setTaskModalOpen(false); loadCaseTasks() } } catch {} }}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
function CaseAttachmentsPanel({ caseId }) {
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const fileInputRef = React.useRef(null)

  const load = React.useCallback(async () => {
    try {
      setLoading(true)
      const resp = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/attachments`))
      const json = await resp.json()
      if (json.success) setItems(json.data || [])
      else throw new Error(json.error || 'Failed to load attachments')
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }, [caseId])

  React.useEffect(() => { load() }, [load])

  const addAttachment = async (file) => {
    if (!file) return
    try {
      const form = new FormData()
      form.append('file', file)
      const resp = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/attachments`), { method: 'POST', body: form })
      const json = await resp.json()
      if (!resp.ok || !json.success) throw new Error(json.error || 'Failed to add attachment')
      await load()
    } catch (e) { setError(e.message) }
  }

  const removeAttachment = async (id) => {
    try {
      await authFetch(getLocalApiUrl(`/api/cases/${caseId}/attachments/${id}`), { method: 'DELETE' })
      await load()
    } catch (e) { setError(e.message) }
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-medium text-gray-900">Attachments</h3>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500">{loading ? 'Loading…' : ''}</div>
          <input type="file" ref={fileInputRef} className="hidden" onChange={(e)=>{ const f=e.target.files?.[0]; if (f) addAttachment(f); e.target.value='' }} />
          <button className="btn-primary" onClick={()=>fileInputRef.current && fileInputRef.current.click()}>Add</button>
        </div>
      </div>
      {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
      
      <div className="mt-4">
        {items.length === 0 ? (
          <p className="text-sm text-gray-500">No attachments yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600">
                  <th className="p-2">File</th>
                  <th className="p-2">Uploaded</th>
                  <th className="p-2">Notes</th>
                  <th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t">
                    <td className="p-2"><a href={it.storage_url} target="_blank" rel="noreferrer" className="text-psg-teal hover:underline">{it.file_name}</a></td>
                    <td className="p-2">{new Date(it.uploaded_at).toLocaleString()}</td>
                    <td className="p-2">{it.notes || '—'}</td>
                    <td className="p-2"><button className="btn-secondary" onClick={()=>removeAttachment(it.id)}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function CaseWorkflowRail({ caseId }) {
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [state, setState] = React.useState(null)
  const [stages, setStages] = React.useState([])
  const [transitions, setTransitions] = React.useState([])
  const [reason, setReason] = React.useState('')

  const load = React.useCallback(async () => {
    try {
      setLoading(true)
      const resp = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/workflows/${active || ''}`))
      const json = await resp.json()
      if (json.success) {
        const data = json.data || json // support both endpoints
        setState(data.state || data.track)
        setStages(data.stages || [])
        setTransitions(data.transitions || [])
      } else {
        throw new Error(json.error || 'Failed to load workflow')
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [caseId])

  React.useEffect(() => { load() }, [load])

  const doTransition = async (t) => {
    try {
      const resp = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/transition`), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transition_key: t.transition_key, reason })
      })
      const json = await resp.json()
      if (!resp.ok || !json.success) throw new Error(json.error || 'Transition failed')
      setReason('')
      await load()
    } catch (e) {
      setError(e.message)
    }
  }

  return (
    <div className="card">
      <div className="text-sm text-gray-500 text-right">{loading ? 'Loading…' : ''}</div>
      {error && (<div className="text-sm text-red-600 mt-2">{error}</div>)}
      <div className="mt-3 overflow-x-auto">
        <div className="flex items-center gap-6">
          {(() => {
            const curOrdinal = (stages.find(st => st.stage_key === state?.current_stage_key)?.ordinal) || 0
            return stages.map((s, idx) => {
              const status = s.ordinal < curOrdinal ? 'done' : (s.ordinal === curOrdinal ? 'current' : 'upcoming')
              const circleCls = status === 'done'
                ? 'bg-psg-teal text-white'
                : status === 'current'
                  ? 'ring-2 ring-psg-teal text-psg-teal bg-white'
                  : 'bg-gray-200 text-gray-400'
              return (
                <div key={s.stage_key} className="flex items-center">
                  {idx>0 && <div className="w-12 h-0.5 bg-gray-300 mr-6" />}
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${circleCls}`}>
                      {status === 'done' ? <CheckIcon className="w-5 h-5" /> : <span className="text-sm font-semibold">{idx+1}</span>}
                    </div>
                    <div className={`text-base ${status==='upcoming' ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>{s.display_name}</div>
                  </div>
                </div>
              )
            })
          })()}
        </div>
      </div>
      {transitions && (
        <div className="mt-4">
          <div className="text-sm text-gray-600 mb-1">Actions</div>
          <div className="flex flex-wrap gap-2">
            {transitions.map((t) => {
              const key = String(t.transition_key || '').toUpperCase()
              const label = key === 'SUBMIT_SMR' ? 'Ready for SMR' : (t.button_label || 'Action')
              return (
                <button key={t.transition_key} className="btn-primary" onClick={() => doTransition(t)}>{label}</button>
              )
            })}
            {(() => {
              const hasResolve = Array.isArray(transitions) && transitions.some(t => {
                const key = String(t.transition_key || '').toUpperCase()
                const label = String(t.button_label || '').toUpperCase()
                return key === 'RESOLVE_FALSE_POS' || key === 'RESOLVE' || label.includes('RESOLVE')
              })
              if (hasResolve) return null
              return (
                <button key="RESOLVE_FALSE_POS_ALWAYS" className="btn-secondary" onClick={() => doTransition({ transition_key: 'RESOLVE_FALSE_POS' })}>Resolve</button>
              )
            })()}
          </div>
          <div className="mt-2">
            <textarea value={reason} onChange={(e)=>setReason(e.target.value)} placeholder="Reason / remarks (if required)" className="input-field w-full" rows={2} />
          </div>
        </div>
      )}
    </div>
  )
}

function CaseWorkflowTimeline({ caseId, onOpenSmrForm, trackId }) {
  const { can } = useAuth()
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [state, setState] = React.useState(null)
  const [stages, setStages] = React.useState([])
  const [transitions, setTransitions] = React.useState([])
  const [reason, setReason] = React.useState('')
  const [notesByStage, setNotesByStage] = React.useState({})
  const [currentDueAt, setCurrentDueAt] = React.useState(null)
  const [smrDecision, setSmrDecision] = React.useState('') // 'resolve' | 'submit_austrac'
  const [transitionDecision, setTransitionDecision] = React.useState('') // selected transition_key for review
  const standardDocs = React.useMemo(() => [
    { key: 'GOVT_ID', label: 'Government ID' },
    { key: 'ADDRESS_PROOF', label: 'Proof of Address' },
    { key: 'BANK_STATEMENT', label: 'Bank Statements' },
    { key: 'PAYSLIPS', label: 'Payslips' },
    { key: 'TAX_RETURNS', label: 'Tax Returns' },
    { key: 'EMPLOYMENT_LETTER', label: 'Employment Letter' },
    { key: 'SOURCE_OF_FUNDS', label: 'Source-of-Funds Evidence' }
  ], [])

  const load = React.useCallback(async () => {
    try {
      setLoading(true)
      const endpoint = trackId ? `/api/cases/${caseId}/workflows/${trackId}` : `/api/cases/${caseId}/workflow`
      const resp = await authFetch(getLocalApiUrl(endpoint))
      const json = await resp.json()
      if (json.success) {
        const data = json.data || json
        setState(data.state || data.track)
        setStages(data.stages || [])
        setTransitions(data.transitions || [])
      } else {
        throw new Error(json.error || 'Failed to load workflow')
      }
      // load escalations/remarks
      try {
        const nresp = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/notes`))
        const njson = await nresp.json()
        if (njson.success && Array.isArray(njson.data)) {
          const map = {}
          njson.data.filter(r => (trackId ? r.track_id === trackId : !r.track_id)).forEach(r => {
            if (!map[r.stage_key]) map[r.stage_key] = []
            map[r.stage_key].push(r)
          })
          setNotesByStage(map)
        }
      } catch (_) {}
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [caseId, trackId])

  React.useEffect(() => { load() }, [load])

  const doTransition = async (t) => {
    try {
      const base = trackId ? `/api/cases/${caseId}/workflows/${trackId}/transition` : `/api/cases/${caseId}/transition`
      const resp = await authFetch(getLocalApiUrl(base), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transition_key: t.transition_key, reason })
      })
      const json = await resp.json()
      if (!resp.ok || !json.success) throw new Error(json.error || 'Transition failed')
      setReason('')
      await load()
    } catch (e) {
      setError(e.message)
    }
  }

  const submitSmrDecision = async () => {
    if (!smrDecision) return
    try {
      setLoading(true)
      setError(null)
      if (smrDecision === 'resolve') {
        const base = trackId ? `/api/cases/${caseId}/workflows/${trackId}/transition` : `/api/cases/${caseId}/transition`
        const resp = await authFetch(getLocalApiUrl(base), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transition_key: 'RESOLVE_FALSE_POS', reason })
        })
        const json = await resp.json()
        if (!resp.ok || !json.success) throw new Error(json.error || 'Resolve failed')
      } else if (smrDecision === 'submit_austrac') {
        await authFetch(getLocalApiUrl(`/api/cases/${caseId}/smr/submit`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: reason || null }) })
      }
      setSmrDecision('')
      setReason('')
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const submitTransitionDecision = async () => {
    if (!transitionDecision) return
    const t = transitions.find(tr => String(tr.transition_key || '').toUpperCase() === String(transitionDecision).toUpperCase())
      || (transitionDecision === 'RESOLVE_FALSE_POS' ? { transition_key: 'RESOLVE_FALSE_POS' } : null)
    if (t) {
      try {
        setLoading(true)
        setError(null)
        await doTransition(t)
        setTransitionDecision('')
        setReason('')
      } finally {
        setLoading(false)
      }
    }
  }

  const curOrdinal = (stages.find(st => st.stage_key === state?.current_stage_key)?.ordinal) || 0
  const dueLabel = currentDueAt ? (()=>{
    const d = new Date(currentDueAt)
    const ms = d.getTime() - Date.now()
    const hrs = Math.round(ms/36e5)
    if (isNaN(hrs)) return null
    return ms >= 0 ? `Due in ${hrs}h` : `Overdue by ${Math.abs(hrs)}h`
  })() : null
  return (
    <div className="card">
      <div className="text-sm text-gray-500 text-right">{loading ? 'Loading…' : ''}</div>
      {error && (<div className="text-sm text-red-600 mt-2">{error}</div>)}

      <div className="mt-3">
        {dueLabel && (
          <div className="mb-2 text-xs">
            <span className={`inline-flex items-center px-2 py-0.5 rounded ${dueLabel.startsWith('Due') ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>{dueLabel}</span>
          </div>
        )}
        <ol className="relative border-l border-gray-200 pl-4">
          {stages.map((s) => {
            const status = s.ordinal < curOrdinal ? 'done' : (s.ordinal === curOrdinal ? 'current' : 'upcoming')
            return (
              <li key={s.stage_key} className="mb-6 ml-2">
                <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ring-2 ring-white ${status==='done' ? 'bg-psg-teal text-white' : status==='current' ? 'bg-white text-psg-teal border border-psg-teal' : 'bg-gray-200 text-gray-500'}`}>
                  {s.ordinal}
                </span>
                <div className={`text-sm ${status==='upcoming' ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                  {(() => {
                    const raw = (s && (s.display_name || s.stage_key)) || ''
                    // Normalize CORE workflow labels: AMLCO -> Compliance Officer
                    return String(raw).replace(/AMLCO/g, 'Compliance Officer').replace(/_/g, ' ')
                  })()}
                </div>
                {notesByStage[s.stage_key] && (
                  <div className="mt-1 space-y-1">
                    {notesByStage[s.stage_key].map(n => (
                      <div key={n.id} className="text-xs text-gray-600">{n.remarks}</div>
                    ))}
                  </div>
                )}
                {status === 'current' && transitions && (
                  <div className="mt-2">
                    {s.stage_key === 'SUBMIT_SMR' ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          {can('case.smr','edit') && (
                            <button type="button" className="text-psg-teal hover:underline" onClick={()=> onOpenSmrForm && onOpenSmrForm()}>Open SMR Form</button>
                          )}
                          {can('case.smr','edit') && can('case.smr','download') && <span className="text-gray-400">|</span>}
                          {can('case.smr','download') && (
                            <button type="button" className="text-psg-teal hover:underline" onClick={async()=>{
                              try {
                                const r = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/smr/download`));
                                const blob = await r.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url; a.download = `SMR_${caseId}.pdf`;
                                document.body.appendChild(a); a.click(); a.remove();
                                window.URL.revokeObjectURL(url);
                              } catch(_){}
                            }}>Download SMR Form</button>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Decision</label>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="radio" name="smrDecision" value="resolve" checked={smrDecision==='resolve'} onChange={()=>setSmrDecision('resolve')} className="text-psg-teal" />
                              <span>Resolve (false positive)</span>
                            </label>
                            {can('case.smr','submit') && (
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input type="radio" name="smrDecision" value="submit_austrac" checked={smrDecision==='submit_austrac'} onChange={()=>setSmrDecision('submit_austrac')} className="text-psg-teal" />
                                <span>Submit to AUSTRAC</span>
                              </label>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Reason / remarks (if required)</label>
                          <textarea value={reason} onChange={(e)=>setReason(e.target.value)} placeholder="Reason / remarks (if required)" className="input-field w-full" rows={2} />
                        </div>
                        <div>
                          <button className="btn-primary" disabled={!smrDecision} onClick={submitSmrDecision}>Submit</button>
                        </div>
                      </div>
                    ) : s.stage_key === 'ECDD_MANAGER_REVIEW' || String(s.stage_key||'').startsWith('ECDD_') ? (
                      <div className="p-4 rounded-lg border border-psg-teal/30 bg-psg-teal/5">
                        <p className="text-sm text-gray-700 mb-2">ECDD is managed separately in the Monitor section.</p>
                        <Link to="/compliance-safe/ecdd" className="text-psg-teal font-medium hover:underline">View ECDD records →</Link>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(() => {
                          const filteredTransitions = Array.isArray(transitions) ? transitions.filter(t => String(t.transition_key || '').toUpperCase() !== 'REQUEST_DOCS') : []
                          const hasResolveInTransitions = transitions?.some(t => {
                            const k = String(t.transition_key||'').toUpperCase()
                            const l = String(t.button_label||'').toUpperCase()
                            return k === 'RESOLVE_FALSE_POS' || k === 'RESOLVE' || l.includes('RESOLVE')
                          })
                          const hasResolveFallback = !String(state?.current_stage_key||'').startsWith('ECDD_') && !hasResolveInTransitions
                          const hasOptions = filteredTransitions.length > 0 || hasResolveFallback
                          return hasOptions && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Decision</label>
                              <div className="flex flex-wrap gap-4">
                                {filteredTransitions.map((t) => {
                                    const key = String(t.transition_key || '').toUpperCase()
                                    const label = key === 'SUBMIT_SMR' ? 'Ready for SMR' : (t.button_label || 'Action')
                                    return (
                                      <label key={t.transition_key} className="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" name="transitionDecision" value={t.transition_key} checked={transitionDecision===t.transition_key} onChange={()=>setTransitionDecision(t.transition_key)} className="text-psg-teal" />
                                        <span>{label}</span>
                                      </label>
                                    )
                                  })}
                                {hasResolveFallback && (
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" name="transitionDecision" value="RESOLVE_FALSE_POS" checked={transitionDecision==='RESOLVE_FALSE_POS'} onChange={()=>setTransitionDecision('RESOLVE_FALSE_POS')} className="text-psg-teal" />
                                    <span>Resolve</span>
                                  </label>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Reason / remarks (if required)</label>
                              <textarea value={reason} onChange={(e)=>setReason(e.target.value)} placeholder="Reason / remarks (if required)" className="input-field w-full" rows={2} />
                            </div>
                            <div>
                              <button className="btn-primary" disabled={!transitionDecision} onClick={submitTransitionDecision}>Submit</button>
                            </div>
                          </>
                          )
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ol>
      </div>
    </div>
  )
}
function NextActionBox({ alert, caseId, onUpdated, onOpenSmrForm }) {
  const safeAlert = alert ?? {}
  const [status, setStatus] = React.useState(() => (safeAlert && safeAlert.status) ? safeAlert.status : 'NEW')
  const [nextAction, setNextAction] = React.useState(() => (safeAlert && safeAlert.extra && safeAlert.extra.workflow_type) ? safeAlert.extra.workflow_type : '')
  const [remarks, setRemarks] = React.useState('')

  const save = async () => {
    try {
      if (safeAlert.id) {
        const resp = await authFetch(getLocalApiUrl(`/api/alerts/${safeAlert.id}/workflow`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status, workflow_type: nextAction, remarks })
        })
        const json = await resp.json()
        if (json && json.success) {
          if (onUpdated) onUpdated(json.data)
          setRemarks('')
        }
      } else if (caseId) {
        const map = { NEW: 'OPEN', IN_PROGRESS: 'INVESTIGATING', REPORTED: 'PENDING_REVIEW', CLOSED: 'CLOSED' }
        await authFetch(getLocalApiUrl(`/api/cases/${caseId}/status`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: map[status] || 'OPEN' })
        })
        setRemarks('')
      }
    } catch (e) {
      // ignore
    }
  }

  return (
    <div className="card">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Next Action</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Next Action</label>
          <select className="select-field w-full" value={nextAction} onChange={(e)=>setNextAction(e.target.value)}>
            <option value="">None</option>
            <option value="EDD">Enhanced Due Diligence (EDD)</option>
            <option value="SMR">Suspicious Matter Report (SMR)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select className="select-field w-full" value={status} onChange={(e)=>setStatus(e.target.value)}>
            <option value="NEW">New</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="REPORTED">Reported</option>
            <option value="CLOSED">Closed</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
          <textarea className="input-field" rows={3} value={remarks} onChange={(e)=>setRemarks(e.target.value)} placeholder="Add analyst remarks..." />
          <div className="mt-2">
            <button onClick={save} className="btn-primary">Submit</button>
          </div>
        </div>

        {/* SMR actions when selected */}
        {nextAction === 'SMR' && (
          <div className="p-3 rounded border bg-amber-50">
            <div className="text-sm font-medium text-amber-800 mb-2">Suspicious Matter Report (AUSTRAC)</div>
            <div className="text-xs text-amber-800 mb-3">Submit within 24h for terrorism financing; within 3 business days for other offences. Ensure the report includes who, what, where, when, why and how; indicators; red flags; and relevant KYC/transaction details.</div>
            <div className="flex gap-2">
              <button onClick={() => onOpenSmrForm && onOpenSmrForm()} className="btn-secondary">Open SMR Form</button>
              <button onClick={async () => {
                try {
                  const r = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/smr/download`));
                  const blob = await r.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `SMR_${caseId}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                } catch (e) {
                  alert('Failed to download SMR: ' + e.message);
                }
              }} className="btn-secondary">Download SMR Form</button>
              <button onClick={async()=>{ try { await authFetch(getLocalApiUrl(`/api/cases/${caseId}/smr/submit`), { method:'POST' }); setStatus('REPORTED'); await save() } catch(_){} }} className="btn-primary">Submit to AUSTRAC</button>
            </div>
          </div>
        )}

        {safeAlert.extra?.remarks && safeAlert.extra.remarks.length > 0 && (
          <div>
            <div className="text-sm text-gray-500 mb-1">Remarks History</div>
            <ul className="text-sm space-y-1">
              {safeAlert.extra.remarks.map((r, idx) => (
                <li key={idx} className="flex items-start justify-between">
                  <span className="text-gray-700">{r.text}</span>
                  <span className="text-gray-400 text-xs ml-4 whitespace-nowrap">{new Date(r.at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

// Previous SMRs for the same member
function PreviousSmrsPanel({ caseId }) {
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true)
        const r = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/previous-smrs`))
        const j = await r.json()
        if (j.success) setItems(j.data || [])
      } catch (_) { setItems([]) }
      finally { setLoading(false) }
    })()
  }, [caseId])
  if (loading) return <div className="card"><div className="text-sm text-gray-500">Loading previous SMRs…</div></div>
  return (
    <div className="card">
      <h3 className="text-lg font-medium text-gray-900 mb-3">Previous SMRs (Same Member)</h3>
      {items.length === 0 ? (
        <div className="text-sm text-gray-500 py-4">No previous SMRs for this member</div>
      ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b text-gray-500 uppercase text-xs">
              <th className="text-left py-2">Case #</th>
              <th className="text-left py-2">Type</th>
              <th className="text-left py-2">Status</th>
              <th className="text-left py-2">Created</th>
              <th className="text-left py-2">AUSTRAC Ref</th>
            </tr>
          </thead>
          <tbody>
            {items.map(r => (
              <tr key={r.id} className="border-b">
                <td className="py-2 font-mono">{r.case_number || r.id}</td>
                <td className="py-2">{r.case_type || '—'}</td>
                <td className="py-2">{r.status || '—'}</td>
                <td className="py-2">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                <td className="py-2">{r.regulatory_ref || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  )
}

// Previous ECDD for the same member
function PreviousEcddPanel({ memberId, memberName }) {
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  React.useEffect(() => {
    if (!memberId) { setLoading(false); return }
    (async () => {
      try {
        setLoading(true)
        const r = await authFetch(getLocalApiUrl(`/api/ecdd?member_id=${encodeURIComponent(memberId)}`))
        const j = await r.json()
        if (j.success) setItems(j.data || [])
      } catch (_) { setItems([]) }
      finally { setLoading(false) }
    })()
  }, [memberId])
  if (!memberId) return null
  if (loading) return <div className="card"><div className="text-sm text-gray-500">Loading previous ECDD…</div></div>
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-medium text-gray-900">Previous ECDD (Same Member)</h3>
        <Link to={`/compliance-safe/ecdd?memberId=${encodeURIComponent(memberId)}`} className="text-sm text-psg-teal hover:underline">
          View all →
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-gray-500 py-4">No previous ECDD for this member</div>
      ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b text-gray-500 uppercase text-xs">
              <th className="text-left py-2">ID</th>
              <th className="text-left py-2">Trigger</th>
              <th className="text-left py-2">Status</th>
              <th className="text-left py-2">Created</th>
              <th className="text-left py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.slice(0, 5).map(r => (
              <tr key={r.id} className="border-b hover:bg-gray-50">
                <td className="py-2 font-mono">{r.id || r.pid}</td>
                <td className="py-2">{r.trigger || r.triggerDescription || '—'}</td>
                <td className="py-2">{r.status || '—'}</td>
                <td className="py-2">{r.triggerDate || r.trigger_date ? new Date(r.triggerDate || r.trigger_date).toLocaleString() : '—'}</td>
                <td className="py-2">
                  <Link
                    to={`/compliance-safe/ecdd/${encodeURIComponent(r.id)}?memberId=${encodeURIComponent(memberId)}${memberName ? `&memberName=${encodeURIComponent(memberName)}` : ''}`}
                    className="text-psg-teal hover:underline text-xs"
                  >
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  )
}

// Evidence + Select Transactions tabs
function EvidenceWithTransactionsPanel({ caseId, onSelectItem, onEvidenceLoaded, onEvidenceAdded }) {
  const [tab, setTab] = React.useState('evidence')
  return (
    <div className="card">
      <div className="flex gap-2 border-b border-gray-200 mb-4">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'evidence' ? 'border-psg-teal text-psg-teal' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          onClick={() => setTab('evidence')}
        >Evidence</button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'transactions' ? 'border-psg-teal text-psg-teal' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          onClick={() => setTab('transactions')}
        >Select Transactions</button>
      </div>
      {tab === 'evidence' && (
        <EvidencePanel caseId={caseId} onSelectItem={onSelectItem} onEvidenceLoaded={onEvidenceLoaded} embedded />
      )}
      {tab === 'transactions' && (
        <SelectTransactionsPanel caseId={caseId} onAdded={onEvidenceAdded} />
      )}
    </div>
  )
}

// Select transactions to add as evidence
function SelectTransactionsPanel({ caseId, onAdded }) {
  const [items, setItems] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [selected, setSelected] = React.useState(new Set())
  const [adding, setAdding] = React.useState(false)
  const [filters, setFilters] = React.useState({ date_from: '', date_to: '', venue: '', search: '', min_amount: '' })
  const [page, setPage] = React.useState(1)
  const [pagination, setPagination] = React.useState({ total: 0, totalPages: 1 })
  const load = React.useCallback(async () => {
    try {
      setLoading(true)
      const params = { page: String(page), limit: '50' }
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v })
      const qs = new URLSearchParams(params)
      const r = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/member-transactions?${qs}`))
      const j = await r.json()
      if (j.success) {
        setItems(j.data || [])
        if (j.pagination) setPagination(j.pagination)
      }
    } catch (_) { setItems([]) }
    finally { setLoading(false) }
  }, [caseId, page, filters])
  React.useEffect(() => { load() }, [load])
  React.useEffect(() => { setPage(1) }, [filters.date_from, filters.date_to, filters.venue, filters.search, filters.min_amount])
  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  const addSelected = async () => {
    if (!selected.size) return
    try {
      setAdding(true)
      const r = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/evidence/add`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transaction_ids: Array.from(selected) })
      })
      const j = await r.json()
      if (j.success) {
        setSelected(new Set())
        onAdded && onAdded()
      } else throw new Error(j.error || 'Failed to add')
    } catch (e) { alert(e.message || 'Failed to add evidence') }
    finally { setAdding(false) }
  }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <input className="input-field text-sm" placeholder="Date from" type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
        <input className="input-field text-sm" placeholder="Date to" type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
        <input className="input-field text-sm" placeholder="Venue" value={filters.venue} onChange={e => setFilters(f => ({ ...f, venue: e.target.value }))} />
        <input className="input-field text-sm" placeholder="Search (EGM, ref)" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
        <input className="input-field text-sm" placeholder="Min amount" type="number" value={filters.min_amount} onChange={e => setFilters(f => ({ ...f, min_amount: e.target.value }))} />
      </div>
      <div className="flex justify-between items-center">
        <button className="btn-primary text-sm" disabled={!selected.size || adding} onClick={addSelected}>
          {adding ? 'Adding…' : `Add ${selected.size} selected to evidence`}
        </button>
        {pagination.totalPages > 1 && (
          <div className="flex gap-2">
            <button className="btn-secondary text-sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</button>
            <span className="text-sm text-gray-500">Page {page} / {pagination.totalPages}</span>
            <button className="btn-secondary text-sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>
      {loading ? (
        <div className="text-sm text-gray-500 py-4">Loading…</div>
      ) : (
        <div className="overflow-x-auto max-h-80 overflow-y-auto border rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left w-12"></th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Venue</th>
                <th className="px-3 py-2 text-left">EGM</th>
                <th className="px-3 py-2 text-left">CashIn</th>
                <th className="px-3 py-2 text-left">CashOut</th>
                <th className="px-3 py-2 text-left">Turnover</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row, idx) => {
                const id = row.TransactionPK || row.transaction_id || idx
                return (
                  <tr key={id} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selected.has(id)} onChange={() => toggle(id)} className="text-psg-teal" />
                    </td>
                    <td className="px-3 py-2">{row.SnapshotTime || row.snapshot_time ? new Date(row.SnapshotTime || row.snapshot_time).toLocaleString() : '—'}</td>
                    <td className="px-3 py-2">{row.Venue || row.venue || '—'}</td>
                    <td className="px-3 py-2">{row.EgmHouseNo || row.egm_house_no || '—'}</td>
                    <td className="px-3 py-2">{row.CashIn ?? row.cash_in ?? '—'}</td>
                    <td className="px-3 py-2">{row.CashOut ?? row.cash_out ?? '—'}</td>
                    <td className="px-3 py-2">{row.Turnover ?? row.turnover ?? '—'}</td>
                  </tr>
                )
              })}
              {!items.length && <tr><td colSpan={7} className="px-3 py-4 text-gray-500">No transactions found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Evidence panel for complex, multi-transaction/session cases
function EvidencePanel({ caseId, onSelectItem, onEvidenceLoaded, embedded }) {
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [evidence, setEvidence] = React.useState(null)
  const [pagination, setPagination] = React.useState({ page: 1, limit: 10, total: 0, totalPages: 0 })
  const [expanded, setExpanded] = React.useState(new Set())
  const [relatedMap, setRelatedMap] = React.useState({})

  const toggleExpand = (rowId) => {
    const next = new Set(expanded)
    if (next.has(rowId)) next.delete(rowId)
    else next.add(rowId)
    setExpanded(next)
  }

  const loadEvidence = React.useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const query = new URLSearchParams({ page: String(pagination.page), limit: String(pagination.limit) })
      const resp = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/evidence?${query.toString()}`))
      const json = await resp.json()
      if (json.success) {
        setEvidence(json.data)
        onEvidenceLoaded && onEvidenceLoaded(json.data)
        if (json.pagination) {
          setPagination((prev) => ({
            ...prev,
            total: json.pagination.total,
            totalPages: json.pagination.totalPages
          }))
        } else if (json.data && Array.isArray(json.data.items)) {
          const total = json.data.items.length
          setPagination((prev) => ({ ...prev, total, totalPages: Math.ceil(total / prev.limit) || 1 }))
        }
      } else {
        throw new Error(json.error || 'Failed to load evidence')
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [caseId, pagination.page, pagination.limit])

  React.useEffect(() => {
    loadEvidence()
  }, [loadEvidence])

  const formatTs = (ts) => {
    if (!ts) return '—'
    try { return new Date(ts).toLocaleString() } catch (_) { return String(ts) }
  }

  const deriveRow = (it, idx) => {
    const id = it.id || it.TransactionPK || it.SessionPK || idx
    const timestamp = it.timestamp || it.TransactionTime || it.EventTime || it.StartTime || it.SnapShotTime || it.created_at
    const type = it.type || it.TransactionType || (it.SessionPK ? 'SESSION' : (it.TransactionPK ? 'TRANSACTION' : 'EVENT'))
    const isSession = !!(it && (it.SessionPK || it.StartTime || it.EndTime))
    const amount = isSession
      ? (it.CreditWin ?? it.TotalWon ?? it.Turnover ?? it.TotalWagered)
      : (it.TransactionAmount ?? it.CumulativeAmount ?? it.CashIn ?? it.CashOut ?? it.TotalWagered ?? it.TotalWon ?? it.Turnover ?? it.CreditWin)
    const member = it.MemberID || it.member_id
    const venue = it.VenueID || it.Venue || it.VenueCode || it.venue
    const device = it.TerminalID || it.EgmHouseNo || it.DeviceId || it.device
    return { id, timestamp, type, amount, member, venue, device }
  }

  const fetchRelatedFor = async (item) => {
    if (!item?.TransactionPK) return
    if (relatedMap[item.TransactionPK]) return
    try {
      const resp = await authFetch(getLocalApiUrl(`/api/transactions/${item.TransactionPK}/related-session`))
      const json = await resp.json()
      if (json.success && json.data) {
        setRelatedMap(prev => ({ ...prev, [item.TransactionPK]: json.data }))
      }
    } catch (_) {}
  }

  // Evaluate simple field-operator-value conditions against an item
  const evaluateCondition = (condition, item) => {
    if (!condition) return false
    if (condition.expression !== undefined) return null // skip complex expressions in UI highlighting
    if (!condition.field || !condition.operator) return false
    const actual = getNested(item, condition.field)
    const expected = condition.value
    switch (condition.operator) {
      case 'greater_than': return Number(actual) > Number(expected)
      case 'greater_than_or_equal': return Number(actual) >= Number(expected)
      case 'less_than': return Number(actual) < Number(expected)
      case 'less_than_or_equal': return Number(actual) <= Number(expected)
      case 'equals': return String(actual) === String(expected)
      case 'not_equals': return String(actual) !== String(expected)
      case 'contains': return String(actual || '').includes(String(expected))
      case 'in': return Array.isArray(expected) ? expected.includes(actual) : String(expected).split(',').map(s=>s.trim()).includes(String(actual))
      default: return false
    }
  }

  const getNested = (obj, path) => {
    if (!obj || !path) return undefined
    return String(path).split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj)
  }

  // Compute client-side total using the same amount derivation used per row
  const clientTotalAmount = React.useMemo(() => {
    try {
      const items = (evidence && evidence.items) || []
      let total = 0
      for (let i = 0; i < items.length; i++) {
        const row = deriveRow(items[i], i)
        const n = Number(row.amount)
        if (Number.isFinite(n)) total += n
      }
      return total
    } catch (_) { return null }
  }, [evidence])

  const wrapperClass = embedded ? '' : 'card'
  return (
    <div className={wrapperClass}>
      {!embedded && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Evidence</h3>
          <div className="text-sm text-gray-500">Computation basis: series analysis</div>
        </div>
      )}

      {loading && (
        <div className="text-sm text-gray-500">Loading evidence…</div>
      )}
      {error && (
        <div className="text-sm text-red-600">{error}</div>
      )}

      {!loading && !error && evidence && (
        <div className="space-y-6">
          {/* Summary */}
          {evidence.summary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-3 rounded border">
                <div className="text-xs text-gray-500">Window</div>
                <div className="text-sm text-gray-900">{formatTs(evidence.summary.from)} → {formatTs(evidence.summary.to)}</div>
              </div>
              <div className="p-3 rounded border">
                <div className="text-xs text-gray-500">Items</div>
                <div className="text-xl font-semibold">{evidence.summary.items_count ?? evidence.items?.length ?? 0}</div>
              </div>
              <div className="p-3 rounded border">
                <div className="text-xs text-gray-500">Distinct Days</div>
                <div className="text-xl font-semibold">{evidence.summary.distinct_days ?? '—'}</div>
              </div>
              <div className="p-3 rounded border">
                <div className="text-xs text-gray-500">Total Amount</div>
                <div className="text-xl font-semibold">{(clientTotalAmount ?? evidence.summary.total_amount) ?? '—'}</div>
              </div>
            </div>
          )}

          {/* Rule Evaluation */}
          {evidence.rule && (
            <div className="p-4 rounded border">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-gray-500">Rule</div>
                  <div className="text-sm font-medium text-gray-900">{evidence.rule.name || evidence.rule.id}</div>
                </div>
                <div className="text-right text-xs text-gray-500">
                  <div>{(evidence.rule.processing_category || '').toUpperCase()}</div>
                  <div>{(evidence.rule.type || '').toUpperCase()}</div>
                </div>
              </div>
              {evidence.rule.conditions && (
                <div className="mt-3 text-xs text-gray-700">
                  <div className="font-semibold mb-1">Logic</div>
                  <pre className="bg-gray-50 border rounded p-2 overflow-auto max-h-48">{JSON.stringify(evidence.rule.conditions, null, 2)}</pre>
                </div>
              )}
            </div>
          )}

          {/* Items table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Venue</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Start</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">End</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(evidence.items || []).map((it, idx) => {
                  const row = deriveRow(it, idx)
                  const rel = it.TransactionPK ? relatedMap[it.TransactionPK] : null
                  const startTs = it.StartTime || rel?.StartTime
                  const endTs = it.EndTime || rel?.EndTime
                  // compute simple condition highlights
                  let conds = []
                  const ruleConds = evidence.rule?.conditions
                  if (ruleConds) {
                    if (ruleConds.type && Array.isArray(ruleConds.conditions)) conds = ruleConds.conditions
                    else conds = [ruleConds]
                  }
                  const evaluations = conds.map(c => evaluateCondition(c, it))
                  const matchCount = evaluations.filter(v => v === true).length
                  const totalSimple = evaluations.filter(v => v !== null).length
                  const matchedAll = totalSimple > 0 && matchCount === totalSimple
                  return (
                    <React.Fragment key={row.id}>
                      <tr className={`hover:bg-gray-50 ${matchedAll ? 'bg-green-50' : matchCount > 0 ? 'bg-yellow-50' : ''}`}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatTs(row.timestamp)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{row.type}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{row.amount ?? '—'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{row.member || '—'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{row.venue || '—'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{row.device || '—'}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatTs(startTs)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{formatTs(endTs)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-right text-sm">
                          <button onClick={() => { toggleExpand(row.id); fetchRelatedFor(it); onSelectItem && onSelectItem(it) }} className="text-primary-600 hover:text-primary-900">
                            {expanded.has(row.id) ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                      {expanded.has(row.id) && (
                        <tr>
                          <td colSpan={7} className="px-4 pb-3">
                            {totalSimple > 0 && (
                              <div className="mb-2 text-xs">
                                <span className="mr-2">Conditions matched:</span>
                                {conds.map((c, i) => {
                                  const res = evaluations[i]
                                  if (res === null) return null
                                  return (
                                    <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full mr-1 ${res ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                      {c.field} {c.operator} {String(c.value)}
                                    </span>
                                  )
                                })}
                              </div>
                            )}
                            {/* Related gaming session for transactions */}
                            {it.TransactionPK && relatedMap[it.TransactionPK] && (
                              <div className="mb-2 text-xs">
                                <div className="font-semibold mb-1">Related Gaming Session</div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div><span className="text-gray-500">Session</span>: <span className="font-mono">{relatedMap[it.TransactionPK].SessionPK}</span></div>
                                  <div><span className="text-gray-500">Venue</span>: <span className="font-mono">{relatedMap[it.TransactionPK].Venue || '—'}</span></div>
                                  <div><span className="text-gray-500">EGM</span>: <span className="font-mono">{relatedMap[it.TransactionPK].EgmHouseNo} {relatedMap[it.TransactionPK].EgmName ? `(${relatedMap[it.TransactionPK].EgmName})` : ''}</span></div>
                                  <div><span className="text-gray-500">Start → End</span>: <span className="font-mono">{formatTs(relatedMap[it.TransactionPK].StartTime)} → {formatTs(relatedMap[it.TransactionPK].EndTime)}</span></div>
                                </div>
                              </div>
                            )}
                            <pre className="text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-64">{JSON.stringify(it, null, 2)}</pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages}
                </div>
                <div className="space-x-2">
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="btn-secondary text-sm"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="btn-secondary text-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && !error && !evidence && (
        <div className="text-sm text-gray-500">No evidence available.</div>
      )}
    </div>
  )
}

// Add SMR Preview Modal at the top level component
const CaseDashboardWithModals = () => {
  const Component = CaseDashboard;
  return <Component />;
};

export default CaseDashboard

// ===============
// SMR Form Drawer
// ===============
function SmrFormDrawer({ draft, lookups, onClose, onSave, caseId }) {
  const [form, setForm] = React.useState(() => draft || {
    partA: { designatedSvc: [], designatedSvcSource: [] },
    partB: { suspReason: [], suspReasonOther: '', groundsForSuspicion: '' },
    partC: { primary: {}, additional: [] },
    partE: { unidentified: false, details: null },
    partF: { transactions: [] },
    partG: { offenceType: null, previousReports: [] },
    partH: { reportingEntity: {}, internalReference: '', contact: {}, reportDate: new Date().toISOString() }
  })

  React.useEffect(() => { if (draft) setForm(draft) }, [draft])

  const toggleMulti = (path, value) => {
    setForm(prev => {
      const arr = new Set(getByPath(prev, path) || [])
      if (arr.has(value)) arr.delete(value); else arr.add(value)
      return setByPath(prev, path, Array.from(arr))
    })
  }

  const setField = (path, value) => setForm(prev => setByPath(prev, path, value))

  const handleSave = async () => { await onSave && onSave(form) }

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose}></div>
      <div className="w-full max-w-3xl h-full bg-white shadow-2xl overflow-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b bg-white">
          <div>
            <div className="text-xs text-gray-500">SMR Draft</div>
            <div className="text-lg font-semibold font-mono">Case {caseId}</div>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={handleSave}>Save Draft</button>
            <button className="btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Transaction Date Range Validation */}
          {form?.meta?.dateRangeValidation && (
            <div className={`rounded-lg p-4 border-2 ${
              form.meta.dateRangeValidation.valid 
                ? 'bg-green-50 border-green-300' 
                : 'bg-red-50 border-red-400'
            }`}>
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 ${form.meta.dateRangeValidation.valid ? 'text-green-600' : 'text-red-600'}`}>
                  {form.meta.dateRangeValidation.valid ? (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <div className={`font-semibold text-sm mb-1 ${
                    form.meta.dateRangeValidation.valid ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {form.meta.dateRangeValidation.valid ? 'SMR Transaction Date Range Valid' : '⚠️ SMR Transaction Date Range INVALID'}
                  </div>
                  <div className={`text-sm mb-2 ${
                    form.meta.dateRangeValidation.valid ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {form.meta.dateRangeValidation.message}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className={form.meta.dateRangeValidation.valid ? 'text-green-700' : 'text-red-700'}>
                      <span className="font-medium">First Transaction:</span> {form.meta.dateRangeValidation.first_date || '-'}
                    </div>
                    <div className={form.meta.dateRangeValidation.valid ? 'text-green-700' : 'text-red-700'}>
                      <span className="font-medium">Last Transaction:</span> {form.meta.dateRangeValidation.last_date || '-'}
                    </div>
                    <div className={form.meta.dateRangeValidation.valid ? 'text-green-700' : 'text-red-700'}>
                      <span className="font-medium">Time Span:</span> {form.meta.dateRangeValidation.span_days} days ({form.meta.dateRangeValidation.span_hours} hours)
                    </div>
                    <div className={form.meta.dateRangeValidation.valid ? 'text-green-700' : 'text-red-700'}>
                      <span className="font-medium">Requirement:</span> ≤ 3 days
                    </div>
                  </div>
                  {!form.meta.dateRangeValidation.valid && (
                    <div className="mt-2 text-xs text-red-700 font-medium">
                      ⚠️ This SMR may not be valid for AUSTRAC submission. Consider excluding transactions outside the 3-day window.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Part A: Designated Services */}
          <section className="card">
            <h3 className="text-md font-medium text-gray-900 mb-3">Designated Services</h3>
            <div className="mb-3">
              <div className="text-xs text-gray-500 mb-1">Service Status</div>
              <div className="flex gap-4 text-sm">
                {['provided','requested','enquired about'].map(opt => (
                  <label key={opt} className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={(form.partA.designatedSvcSource||[]).includes(opt)} onChange={()=>toggleMulti('partA.designatedSvcSource', opt)} />
                    <span className="capitalize">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {(lookups.services || []).map(s => (
                <label key={s.code} className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={(form.partA.designatedSvc||[]).includes(s.code)} onChange={()=>toggleMulti('partA.designatedSvc', s.code)} />
                  <span>{s.description}</span>
                </label>
              ))}
            </div>
          </section>

          {/* Part B: Suspicion Reasons */}
          <section className="card">
            <h3 className="text-md font-medium text-gray-900 mb-3">Suspicion Reasons & Grounds</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {(lookups.reasons || []).map(r => (
                <label key={r.code} className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={(form.partB.suspReason||[]).includes(r.code)} onChange={()=>toggleMulti('partB.suspReason', r.code)} />
                  <span>{r.description}</span>
                </label>
              ))}
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Other reason(s)</label>
              <input className="input-field" value={form.partB.suspReasonOther||''} onChange={(e)=>setField('partB.suspReasonOther', e.target.value)} />
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Grounds for Suspicion</label>
              <textarea className="input-field" rows={4} value={form.partB.groundsForSuspicion||''} onChange={(e)=>setField('partB.groundsForSuspicion', e.target.value)} placeholder="Describe the nature and circumstances…" />
            </div>
          </section>

          {/* Part F: Transactions (read-only list from draft) */}
          <section className="card">
            <h3 className="text-md font-medium text-gray-900 mb-3">Transactions</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Reference</th>
                    <th className="px-3 py-2 text-left">Amount</th>
                    <th className="px-3 py-2 text-left">Cash</th>
                    <th className="px-3 py-2 text-left">Completed</th>
                    <th className="px-3 py-2 text-left">Currency</th>
                    <th className="px-3 py-2 text-left">Venue</th>
                    <th className="px-3 py-2 text-left">Device</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(form.partF.transactions||[]).map((t, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2">{t.txnDate ? new Date(t.txnDate).toLocaleString() : '—'}</td>
                      <td className="px-3 py-2">{t.txnType || '—'}</td>
                      <td className="px-3 py-2">{t.txnRefNo || '—'}</td>
                      <td className="px-3 py-2">{t.txnAmount ?? '—'}</td>
                      <td className="px-3 py-2">{t.cashAmount ?? '—'}</td>
                      <td className="px-3 py-2">{t.txnCompleted || '—'}</td>
                      <td className="px-3 py-2">{t.txnCurrency || '—'}</td>
                      <td className="px-3 py-2">{t.venue || '—'}</td>
                      <td className="px-3 py-2">{t.device || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Part G: Offence Type */}
          <section className="card">
            <h3 className="text-md font-medium text-gray-900 mb-3">Offence Type</h3>
            <select className="select-field" value={form.partG.offenceType || ''} onChange={(e)=>setField('partG.offenceType', e.target.value)}>
              <option value="">Select an offence</option>
              {(lookups.offences || []).map(o => (
                <option key={o.code} value={o.code}>{o.description}</option>
              ))}
            </select>
          </section>

          {/* Part H: Reporting Entity (read-only defaults) */}
          <section className="card">
            <h3 className="text-md font-medium text-gray-900 mb-3">Reporting Entity</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Entity</span><div className="font-mono">{form.partH?.reportingEntity?.name || '—'}</div></div>
              <div><span className="text-gray-500">Reference</span><div className="font-mono">{form.partH?.internalReference || '—'}</div></div>
              <div className="col-span-2"><span className="text-gray-500">Address</span><div className="font-mono">{form.partH?.reportingEntity?.address || '—'}</div></div>
              <div><span className="text-gray-500">Report Date</span><div className="font-mono">{formatSmrReportDateTime(form.partH?.reportDate, form.partH?.reportDateDisplay)}</div></div>
            </div>
          </section>

        </div>
      </div>
    </div>
  )
}

// Helpers for nested form state
function getByPath(obj, path) {
  return String(path).split('.').reduce((acc, k) => (acc && (k in acc) ? acc[k] : undefined), obj)
}
function setByPath(obj, path, value) {
  const keys = String(path).split('.')
  const next = Array.isArray(obj) ? [...obj] : { ...obj }
  let cur = next
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i]
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : (typeof cur[k] === 'object' && cur[k] !== null ? { ...cur[k] } : {})
    cur = cur[k]
  }
  cur[keys[keys.length - 1]] = value
  return next
}